import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, ChevronDown, ChevronUp, WifiOff } from "lucide-react";
import apiPath from "../../constants/apiPath";
import logoImg from "../../assets/images/logo-for-logins.png";
import { useAppContext } from "../../context/AppContext";

const DAY_NAMES = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BLOCK_KEYS = ["A_BLOCK", "B_BLOCK", "C_BLOCK", "D_BLOCK", "E_BLOCK", "F_BLOCK"];

// ── Helpers ──────────────────────────────────────────────────────────────────

function getMonthGrid(year, month) {
  // month is 0-indexed
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const totalDays = lastDay.getDate();

  // getDay(): 0=Sun ... 6=Sat  -> convert to Mon=0
  let startDow = (firstDay.getDay() + 6) % 7;

  const weeks = [];
  let week = new Array(7).fill(null);
  let dayNum = 1;

  for (let i = startDow; i < 7 && dayNum <= totalDays; i++) {
    week[i] = dayNum++;
  }
  weeks.push(week);

  while (dayNum <= totalDays) {
    week = new Array(7).fill(null);
    for (let i = 0; i < 7 && dayNum <= totalDays; i++) {
      week[i] = dayNum++;
    }
    weeks.push(week);
  }
  return weeks;
}

function dateToDayName(year, month, day) {
  const d = new Date(year, month, day);
  return DAY_NAMES[(d.getDay() + 6) % 7];
}

function dateToDateString(year, month, day) {
  // Use local date components, NOT toISOString() which converts to UTC
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatDateHeader(year, month, day) {
  const d = new Date(year, month, day);
  return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function isToday(year, month, day) {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

function isPast(year, month, day) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(year, month, day);
  return d < now;
}

// Helper: resolve writable day data from cloned full_week — checks dates first, then top-level, creates under dates if new
function resolveWritable(fullWeek, dayKey) {
  if (!fullWeek) return undefined;
  // If date-keyed data exists, use it
  if (fullWeek.dates?.[dayKey]) return fullWeek.dates[dayKey];
  // If top-level date key exists (shouldn't happen but handle it)
  if (fullWeek[dayKey] && /^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
    if (!fullWeek.dates) fullWeek.dates = {};
    fullWeek.dates[dayKey] = fullWeek[dayKey];
    return fullWeek.dates[dayKey];
  }
  // If it's a date key, check if day-name data exists and copy it
  if (/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
    const dayName = new Date(dayKey + 'T12:00:00').toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
    if (!fullWeek.dates) fullWeek.dates = {};
    if (fullWeek[dayName]) {
      // Copy existing day-name data to date key
      fullWeek.dates[dayKey] = JSON.parse(JSON.stringify(fullWeek[dayName]));
    } else {
      fullWeek.dates[dayKey] = { data: {} };
    }
    return fullWeek.dates[dayKey];
  }
  // Legacy day-name key
  if (fullWeek[dayKey]) return fullWeek[dayKey];
  return undefined;
}

function emptyExercise() {
  return { name: "", sets: "", reps: "", tempo: "", notes: "", isHeader: false, isSplit: false };
}

function emptyBlock(key) {
  return {
    blockName: key.replace("_", " "),
    title: "",
    description: "",
    duration: "",
    timeSegments: [],
    showSets: true,
    showReps: true,
    showTempo: true,
    variants: null,
    activeVariant: "A",
    block_transition: 0,
    tasks: [emptyExercise()],
  };
}

function ensureBlockFields(block) {
  return {
    ...block,
    showSets: block.showSets !== undefined ? block.showSets : true,
    showReps: block.showReps !== undefined ? block.showReps : true,
    showTempo: block.showTempo !== undefined ? block.showTempo : true,
    variants: block.variants || null,
    activeVariant: block.activeVariant || "A",
    timeSegments: block.timeSegments || [],
    block_transition: block.block_transition || 0,
    variantTimings: block.variantTimings || {},
  };
}

function parseSegmentsString(str) {
  if (!str || typeof str !== "string") return [];
  // Parse all numbers — keep zeros so 40/0/40/0 = [40,0,40,0] not [40,40]
  const parts = str.split("/").map((s) => parseInt(s.trim(), 10));
  const segments = [];
  for (let i = 0; i < parts.length; i += 2) {
    const workingTime = parts[i];
    if (isNaN(workingTime) || workingTime <= 0) continue; // work must be > 0
    // restTime can be 0 (back-to-back intervals, EMOM-style)
    const restTime = (i + 1 < parts.length && !isNaN(parts[i + 1])) ? parts[i + 1] : 0;
    const duration = workingTime + restTime;
    const wLabel = `${String(Math.floor(workingTime / 60)).padStart(2, "0")}:${String(workingTime % 60).padStart(2, "0")}`;
    const rLabel = `${String(Math.floor(restTime / 60)).padStart(2, "0")}:${String(restTime % 60).padStart(2, "0")}`;
    segments.push({ workingTime, restTime, duration, label: `${wLabel}+${rLabel}` });
  }
  return segments;
}

function segmentsToDuration(segments) {
  if (!segments || segments.length === 0) return "";
  const total = segments.reduce((sum, s) => sum + s.duration, 0);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function segmentsToInputString(segments) {
  if (!segments || !Array.isArray(segments) || segments.length === 0) return "";
  const parts = [];
  // Always push rest (even 0) so multi-segment back-to-back parses ("10/0/12/0")
  // survive the round-trip. parseSegmentsString reads in work/rest pairs by index,
  // so omitting a 0 rest collapses two segments into one (work=10, rest=12).
  segments.forEach((s) => {
    parts.push(String(s.workingTime));
    parts.push(String(s.restTime || 0));
  });
  // Cosmetic: drop a single trailing 0 when the last segment has no rest -
  // "40/10/40/0" displays as "40/10/40". parseSegmentsString still gets the same shape.
  while (parts.length > 1 && parts[parts.length - 1] === "0") {
    parts.pop();
  }
  return parts.join("/");
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #0d0d1a 100%)",
    color: "#e0e0e0",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  topBar: {
    background: "rgba(13,13,26,0.97)",
    borderBottom: "1px solid rgba(252,220,8,0.12)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    padding: "12px 24px",
  },
  topBarInner: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },
  topBarRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#fcdc08",
    letterSpacing: "1px",
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  monthLabel: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#fff",
    minWidth: "180px",
    textAlign: "center",
  },
  navBtn: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px",
    color: "#fff",
    padding: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  userArea: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  logoutBtn: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "6px",
    color: "#e0e0e0",
    padding: "5px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
  },
  calendarWrap: {
    padding: "20px 24px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
  },
  dayHeader: {
    padding: "8px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "12px",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  dayCell: (isSelected, isTodayCell, isPastCell) => ({
    minHeight: "90px",
    padding: "8px",
    background: isSelected
      ? "rgba(0,74,173,0.25)"
      : isPastCell
      ? "rgba(255,255,255,0.02)"
      : "rgba(255,255,255,0.04)",
    border: isSelected
      ? "2px solid #004aad"
      : isTodayCell
      ? "2px solid #fcdc08"
      : "1px solid rgba(255,255,255,0.06)",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    opacity: isPastCell ? 0.5 : 1,
    position: "relative",
  }),
  dayNumber: (isTodayCell) => ({
    fontSize: "14px",
    fontWeight: isTodayCell ? 700 : 500,
    color: isTodayCell ? "#fcdc08" : "#ccc",
    marginBottom: "6px",
  }),
  blockBadge: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: 600,
    marginRight: "3px",
    marginBottom: "2px",
    background: "rgba(0,74,173,0.3)",
    color: "#6fa8ff",
  },
  emptyCell: {
    minHeight: "90px",
    padding: "8px",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: "8px",
  },
  // Day panel
  dayPanel: {
    maxWidth: "1200px",
    margin: "0 auto 40px",
    padding: "0 24px",
  },
  dayPanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
    padding: "12px 16px",
    background: "rgba(255,255,255,0.04)",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  dayTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#fff",
  },
  copyBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(252,220,8,0.1)",
    border: "1px solid rgba(252,220,8,0.3)",
    borderRadius: "8px",
    color: "#fcdc08",
    padding: "6px 14px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
  },
  addBlockBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(0,74,173,0.15)",
    border: "1px solid rgba(0,74,173,0.4)",
    borderRadius: "8px",
    color: "#6fa8ff",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    marginTop: "12px",
  },
  // Block card
  blockCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    marginBottom: "12px",
    overflow: "hidden",
  },
  blockHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: "rgba(0,74,173,0.1)",
    cursor: "pointer",
    userSelect: "none",
  },
  blockTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  blockBadgeLg: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 700,
    background: "rgba(0,74,173,0.35)",
    color: "#6fa8ff",
    letterSpacing: "0.5px",
  },
  blockActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  deleteBlockBtn: {
    background: "none",
    border: "none",
    color: "#ff5555",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  blockBody: {
    padding: "16px",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 120px 1fr",
    gap: "12px",
    marginBottom: "12px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "6px",
    padding: "7px 10px",
    color: "#e0e0e0",
    fontSize: "13px",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    boxSizing: "border-box",
  },
  // Variant tabs
  variantTabs: {
    display: "flex",
    gap: "4px",
    marginBottom: "12px",
  },
  variantTab: (isActive) => ({
    padding: "4px 14px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    background: isActive ? "#004aad" : "rgba(255,255,255,0.06)",
    color: isActive ? "#fff" : "#888",
    border: isActive ? "1px solid #004aad" : "1px solid rgba(255,255,255,0.08)",
    transition: "all 0.15s ease",
  }),
  // Exercise table
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 2px",
  },
  th: {
    textAlign: "left",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  tdInput: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "4px",
    padding: "6px 8px",
    color: "#e0e0e0",
    fontSize: "13px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  headerRow: {
    background: "rgba(252,220,8,0.08)",
  },
  headerInput: {
    background: "rgba(252,220,8,0.1)",
    border: "1px solid rgba(252,220,8,0.25)",
    borderRadius: "4px",
    padding: "6px 8px",
    color: "#fcdc08",
    fontSize: "13px",
    fontWeight: 700,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  hCheckbox: (checked) => ({
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    border: checked ? "2px solid #fcdc08" : "2px solid rgba(255,255,255,0.15)",
    background: checked ? "rgba(252,220,8,0.2)" : "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fcdc08",
    fontSize: "12px",
    fontWeight: 700,
  }),
  addExRow: {
    background: "none",
    border: "1px dashed rgba(255,255,255,0.12)",
    borderRadius: "6px",
    color: "#666",
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "6px",
  },
  deleteExBtn: {
    background: "none",
    border: "none",
    color: "#ff5555",
    cursor: "pointer",
    padding: "4px",
    opacity: 0.5,
    display: "flex",
    alignItems: "center",
  },
  saveBanner: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    background: "rgba(0,74,173,0.9)",
    color: "#fff",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 500,
    zIndex: 200,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
    transition: "opacity 0.3s ease",
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    color: "#888",
    fontSize: "16px",
  },

  offlineBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    background: 'rgba(234,179,8,0.15)',
    border: '1px solid rgba(234,179,8,0.4)',
    borderRadius: '6px',
    color: '#fbbf24',
    padding: '3px 10px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
};

// ── Component ────────────────────────────────────────────────────────────────

const Programme = () => {
  const navigate = useNavigate();

  // Auth
  const [userName, setUserName] = useState("");
  const [studioLogo, setStudioLogo] = useState(null);

  // Calendar state
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(null); // { day, month, year }

  // Data — shared via AppContext so Dashboard stays in sync
  const { weeklyPlan, setWeeklyPlan } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [justSaved, setJustSaved] = useState(false);

  // Collapsed blocks
  const [collapsedBlocks, setCollapsedBlocks] = useState({});

  // Debounce refs
  const saveTimers = useRef({});
  const pendingSaves = useRef({});
  // Timestamp of the most recent SUCCESSFUL local save - used by the WS live-sync
  // handler to skip refetches we just triggered ourselves. Avoids the ID-type
  // mismatch where localStorage.user.id is a Studios UUID but the backend broadcasts
  // a Django integer id, which would otherwise make every save look like an external
  // edit and flicker the calendar.
  const lastLocalSaveAt = useRef(0);

  // Flush all pending saves immediately when navigating away —
  // prevents the 500ms debounce being abandoned mid-navigation
  useEffect(() => {
    return () => {
      const token = localStorage.getItem("accessToken");
      // Cancel all pending debounce timers
      Object.values(saveTimers.current).forEach((t) => clearTimeout(t));
      // Fire every queued save immediately with keepalive so it survives page transition
      Object.values(pendingSaves.current).forEach((payload) => {
        fetch(`${apiPath.baseURL}${apiPath.updateBlock}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      });
    };
  }, []);

  // ── Auth check ──────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const parsed = JSON.parse(userData);
        setUserName(parsed.name || parsed.email || "");
      }
    } catch {
      // ignore
    }
    // Fetch studio logo
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.profile}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      if (res.ok) {
        const data = await res.json();
        const logo = data?.data?.logo_image || data?.logo_image || null;
        if (logo) setStudioLogo(logo);
      }
    } catch {
      // ignore — logo is decorative
    }
    })();
  }, [navigate]);

  // ── Fetch weekly plan ───────────────────────────────────────────────────

  const fetchWeeklyPlan = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.getWeeklyPlan}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === 200) {
        setWeeklyPlan(data);
        setIsOffline(false);
        // Cache for offline use
        try { localStorage.setItem("fh_programme_cache", JSON.stringify(data)); } catch { /* storage full */ }
      } else if (data.status === 403) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userData");
        navigate("/login");
      }
    } catch {
      // Network failure — try cache
      const cached = localStorage.getItem("fh_programme_cache");
      if (cached) {
        try {
          setWeeklyPlan(JSON.parse(cached));
          setIsOffline(true);
        } catch { /* corrupt cache */ }
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // If Dashboard already loaded data into context, use it — don't overwrite with a separate API fetch
    if (weeklyPlan) {
      setLoading(false);
    } else {
      fetchWeeklyPlan();
    }
  }, [fetchWeeklyPlan]);

  // === Multi-dashboard live sync ===
  // Subscribe to WS. When another dashboard saves a plan change for this gym,
  // refetch silently. Skip refetch when this user originated the save or when
  // they're mid-edit (focus inside an input/textarea).
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let syncTimer = null;
    let closed = false;

    const connect = () => {
      if (closed) return;
      try {
        const baseUrl = apiPath.baseURL
          .replace("/api/v1/", "/")
          .replace("http://", "ws://")
          .replace("https://", "wss://");
        const wsUrl = `${baseUrl}ws/preview/?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          // keepalive ping every 25s to keep some proxies happy
          pingTimer = setInterval(() => {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) {}
          }, 25000);
        };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type !== "plan_updated") return;
            const myUser = JSON.parse(localStorage.getItem("user") || "{}");
            const myId = String(myUser.id || "");
            const srcId = String(data.source_user_id || "");
            if (srcId && srcId === myId) return; // id-match: we originated this save
            // Timestamp fallback for the common Studios-UUID vs Django-int mismatch
            if (Date.now() - lastLocalSaveAt.current < 12000) return;
            if (syncTimer) clearTimeout(syncTimer);
            syncTimer = setTimeout(() => {
              const ae = document.activeElement;
              const isEditing = ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable);
              if (isEditing) {
                // re-arm: try again after focus leaves or in 5s
                syncTimer = setTimeout(() => fetchWeeklyPlan(), 5000);
                return;
              }
              fetchWeeklyPlan();
            }, 400);
          } catch (_) {}
        };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (!closed) {
            reconnectTimer = setTimeout(connect, 3000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      closed = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (syncTimer) clearTimeout(syncTimer);
      if (ws) try { ws.close(); } catch (_) {}
    };
  }, [fetchWeeklyPlan]);

  const [isOffline, setIsOffline] = useState(false);

  // Master push state
  const [linkedStudios, setLinkedStudios] = useState([]);
  const [isPushing, setIsPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  // showPushConfirm is now either null (closed) or 'A'/'B'/'C' (which variant the user clicked to push)
  const [showPushConfirm, setShowPushConfirm] = useState(null);
  // Variant labels keyed by A/B/C - falls back to generic labels for non-master accounts.
  // For S30 AU master (Sophie) the DB returns {A: "S30X", B: "S30", C: "Variant C"}.
  const [variantLabels, setVariantLabels] = useState({ A: "Variant A", B: "Variant B", C: "Variant C" });
  // Variant-aware clipboard — holds copied day data + source date until pasted.
  const [clipboard, setClipboard] = useState(null);

  // Fetch linked studios + variant labels on mount — only master accounts get studio results.
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;
      try {
        const res = await fetch(`${apiPath.baseURL}${apiPath.getStudioLinks}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.status === 200 && data.studios?.length > 0) {
          setLinkedStudios(data.studios);
        }
      } catch {
        // Not a master account or network error — silently ignore
      }
      try {
        const ngRes = await fetch(`${apiPath.baseURL}gym_owner/network/group`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ngData = await ngRes.json();
        if (ngData?.status === 200 && ngData.data?.variant_labels) {
          const vl = ngData.data.variant_labels;
          setVariantLabels({
            A: vl.A || "Variant A",
            B: vl.B || "Variant B",
            C: vl.C || "Variant C",
          });
        }
      } catch {
        // Non-master or network error — keep default labels.
      }
    })();
  }, []);

  // Push to a single variant subset (A or B). variantKey is required - we no
  // longer expose a single "push all" button so a typo or missing arg fails loud.
  // Flush every queued debounced save and WAIT for it to commit before we
  // let the caller proceed. The push endpoint reads the master's SAVED plan
  // from the DB, so a push fired while a 500ms autosave is still pending would
  // ship the pre-edit program to the studios ("reverts to what was there
  // before"). Awaiting the in-flight PATCHes guarantees master is current first.
  const flushPendingSaves = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    Object.values(saveTimers.current).forEach((t) => clearTimeout(t));
    saveTimers.current = {};
    const payloads = Object.values(pendingSaves.current);
    pendingSaves.current = {};
    if (!payloads.length) return;
    lastLocalSaveAt.current = Date.now();
    await Promise.all(
      payloads.map((payload) =>
        fetch(`${apiPath.baseURL}${apiPath.updateBlock}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }).catch(() => {})
      )
    );
    lastLocalSaveAt.current = Date.now();
  }, []);

  const handlePushToStudios = async (variantKey) => {
    setIsPushing(true);
    setShowPushConfirm(null);
    setPushResult(null);
    // Commit any in-flight edits to the master plan BEFORE the push reads it.
    await flushPendingSaves();
    const token = localStorage.getItem("accessToken");
    try {
      // variantKey === "ALL" or null means push to every studio (no filter)
      const url = (variantKey && variantKey !== "ALL")
        ? `${apiPath.baseURL}${apiPath.pushToStudios}?variant=${encodeURIComponent(variantKey)}`
        : `${apiPath.baseURL}${apiPath.pushToStudios}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.status === 200) {
        setPushResult({ success: true, message: data.message });
      } else {
        setPushResult({ success: false, message: data.message || "Push failed" });
      }
    } catch {
      setPushResult({ success: false, message: "Network error" });
    } finally {
      setIsPushing(false);
      setTimeout(() => setPushResult(null), 5000);
    }
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
    setSelectedDate(null);
  };

  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });

  // ── Get day data from weekly plan ───────────────────────────────────────

  const getDayData = useCallback(
    (dayKey) => {
      if (!weeklyPlan?.full_week) return null;
      const fw = weeklyPlan.full_week;
      const dateData = fw.dates?.[dayKey];
      const topData = fw[dayKey];
      let dayNameData;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
        const dn = new Date(dayKey + 'T12:00:00').toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
        dayNameData = fw[dn];
      }
      // If a date entry exists with block data, use it exclusively for blocks.
      // Never merge template blocks into a date entry — it causes phantom blocks
      // that appear editable but whose edits are silently dropped (resolveWritable
      // can't find them in the dates entry). Metadata (name, color, image) still
      // falls back from the day template when not set on the date entry.
      if (dateData && dayNameData) {
        const merged = { ...dayNameData, ...dateData };
        if (dateData.data && Object.keys(dateData.data).length > 0) {
          // Date entry has blocks — use them exclusively, no template merging
          merged.data = dateData.data;
        } else if (dayNameData.data) {
          // Date entry exists but has no blocks — fall back to template blocks
          merged.data = dayNameData.data;
        }
        return merged;
      }
      return dateData || topData || dayNameData || null;
    },
    [weeklyPlan]
  );

  const getBlocksForDay = useCallback(
    (dayName) => {
      const dayData = getDayData(dayName);
      if (!dayData?.data) return [];
      return Object.keys(dayData.data).filter((k) => k.endsWith("_BLOCK")).sort();
    },
    [getDayData]
  );

  // ── Check if a calendar day has programme data ─────────────────────────

  const dayHasData = useCallback(
    (year, month, day) => {
      const dateStr = dateToDateString(year, month, day);
      const dayName = dateToDayName(year, month, day);
      // Check date-keyed data first, fall back to day-name-keyed
      const blocks = getBlocksForDay(dateStr);
      return blocks.length > 0 || getBlocksForDay(dayName).length > 0;
    },
    [getBlocksForDay]
  );

  // ── Select a day ────────────────────────────────────────────────────────

  const handleSelectDay = (day) => {
    if (!day) return;
    if (selectedDate && selectedDate.day === day && selectedDate.month === viewMonth && selectedDate.year === viewYear) {
      setSelectedDate(null);
    } else {
      setSelectedDate({ day, month: viewMonth, year: viewYear });
    }
  };

  // ── Debounced save ──────────────────────────────────────────────────────

  const autoSave = useCallback(
    (dayName, updates) => {
      const token = localStorage.getItem("accessToken");
      const saveKey = updates.block || "general";

      // Mark the edit the instant it happens (not just when the debounced save fires)
      // so an incoming plan_updated from another save/publish cannot refetch over the
      // top of what the user is actively typing ("deletes back to what was there").
      lastLocalSaveAt.current = Date.now();
      pendingSaves.current[`${dayName}_${saveKey}`] = { day: dayName, ...updates };

      if (saveTimers.current[`${dayName}_${saveKey}`]) {
        clearTimeout(saveTimers.current[`${dayName}_${saveKey}`]);
      }

      saveTimers.current[`${dayName}_${saveKey}`] = setTimeout(async () => {
        const payload = pendingSaves.current[`${dayName}_${saveKey}`];
        delete pendingSaves.current[`${dayName}_${saveKey}`];

        // Mark BEFORE the fetch - the backend broadcasts plan_updated during the
        // save itself, so the WS event can arrive while this fetch promise is still
        // pending. Setting the timestamp upfront ensures the bail-out check in the
        // WS handler sees a fresh timestamp regardless of network/event ordering.
        lastLocalSaveAt.current = Date.now();

        try {
          const res = await fetch(`${apiPath.baseURL}${apiPath.updateBlock}`, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const result = await res.json();
          if (result.status === 200) {
            lastLocalSaveAt.current = Date.now();
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 1200);
          }
        } catch {
          // silent
        }
      }, 500);
    },
    []
  );

  // ── Block CRUD ──────────────────────────────────────────────────────────

  const addBlock = useCallback(
    async (dayName) => {
      const dayData = getDayData(dayName);
      const existingBlocks = dayData?.data ? Object.keys(dayData.data).filter((k) => k.endsWith("_BLOCK")) : [];
      const nextKey = BLOCK_KEYS.find((k) => !existingBlocks.includes(k));
      if (!nextKey) return;

      const token = localStorage.getItem("accessToken");
      try {
        const res = await fetch(`${apiPath.baseURL}${apiPath.addBlock}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            day: dayName,
            block_name: nextKey,
            // Send a proper title (e.g. "A BLOCK") so the backend doesn't default to "New Block",
            // which then renders as "NEW BLOCK" in the Dashboard header.
            block_title: `${nextKey.replace("_BLOCK", "")} BLOCK`,
          }),
        });
        const result = await res.json();
        if (result.status === 200 || result.status === 201) {
          await fetchWeeklyPlan();
        }
      } catch {
        // Fallback: update local state
        setWeeklyPlan((prev) => {
          if (!prev) return prev;
          const newData = JSON.parse(JSON.stringify(prev));
          const wd = resolveWritable(newData.full_week, dayName);
          if (!wd.data) wd.data = {};
          wd.data[nextKey] = emptyBlock(nextKey);
          return newData;
        });
      }
    },
    [getDayData, fetchWeeklyPlan]
  );

  const deleteBlock = useCallback(
    async (dayName, blockKey) => {
      const token = localStorage.getItem("accessToken");
      try {
        const res = await fetch(`${apiPath.baseURL}${apiPath.deleteBlock}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ day: dayName, block_name: blockKey }),
        });
        const result = await res.json();
        if (result.status === 200) {
          await fetchWeeklyPlan();
        }
      } catch {
        // Fallback: local remove
        setWeeklyPlan((prev) => {
          if (!prev) return prev;
          const newData = JSON.parse(JSON.stringify(prev));
          const wd = resolveWritable(newData.full_week, dayName);
          if (wd?.data?.[blockKey]) {
            delete wd.data[blockKey];
          }
          return newData;
        });
      }
    },
    [fetchWeeklyPlan]
  );

  // ── Copy from previous day ─────────────────────────────────────────────

  const copyFromPreviousDay = useCallback(
    (currentDateStr) => {
      if (!selectedDate) return;
      // Get previous day's date string
      const currentDate = new Date(selectedDate.year, selectedDate.month, selectedDate.day);
      const prevDate = new Date(currentDate);
      prevDate.setDate(prevDate.getDate() - 1);
      const prevDateStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}-${String(prevDate.getDate()).padStart(2, '0')}`;
      const prevDayName = DAY_NAMES[(prevDate.getDay() + 6) % 7];
      // Try date-keyed data first, then day-name-keyed
      const prevData = getDayData(prevDateStr) || getDayData(prevDayName);
      if (!prevData?.data) return;

      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        if (!newData.full_week.dates) newData.full_week.dates = {};
        if (!newData.full_week.dates[currentDateStr]) {
          newData.full_week.dates[currentDateStr] = {};
        }
        newData.full_week.dates[currentDateStr].data = JSON.parse(JSON.stringify(prevData.data));
        return newData;
      });

      // Save each copied block
      Object.keys(prevData.data).forEach((blockKey) => {
        autoSave(currentDateStr, {
          block: blockKey,
          block_data: ensureBlockFields(prevData.data[blockKey]),
        });
      });
    },
    [getDayData, autoSave, selectedDate]
  );

  // selectedDayName must be available to copy/paste callbacks below
  const selectedDayName = selectedDate ? dateToDateString(selectedDate.year, selectedDate.month, selectedDate.day) : null;

  // ── Variant-aware clipboard: copy current day, paste into a specific variant ─

  // Snapshot the currently-viewed day's data into clipboard state. Clipboard
  // persists across day navigation so the user can copy on Monday, navigate to
  // Friday, then paste into Friday's chosen variant.
  const copyDayToClipboard = useCallback(() => {
    if (!selectedDate) return;
    const dateStr = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}`;
    const data = getDayData(dateStr) || getDayData(selectedDayName);
    if (!data?.data || Object.keys(data.data).length === 0) {
      setPushResult({ success: false, message: 'Nothing to copy on this day' });
      setTimeout(() => setPushResult(null), 3000);
      return;
    }
    const sourceLabel = new Date(selectedDate.year, selectedDate.month, selectedDate.day)
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    setClipboard({
      sourceDate: dateStr,
      sourceLabel,
      data: JSON.parse(JSON.stringify(data.data)),
    });
  }, [selectedDate, selectedDayName, getDayData]);

  // Paste the clipboard's per-block tasks for `targetVariant` into the currently-
  // selected day. Other variants on the target day are preserved untouched.
  // Block metadata (blockName, duration) is only copied across when the target
  // block doesn't already exist; this avoids overwriting names the studio set.
  const pasteVariantFromClipboard = useCallback(
    (targetVariant) => {
      if (!clipboard || !selectedDate) return;
      const targetDateStr = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}`;
      const source = clipboard.data;
      if (!source) return;

      const currentTarget = getDayData(targetDateStr) || { data: {} };
      const currentBlocks = currentTarget.data || {};

      const mergedBlocks = {};
      Object.keys(source).forEach((blockKey) => {
        const sourceBlock = source[blockKey];
        // Pull source's tasks for the target variant; fall back to legacy `tasks`
        let sourceTasks = [];
        if (sourceBlock?.variants && Array.isArray(sourceBlock.variants[targetVariant])) {
          sourceTasks = sourceBlock.variants[targetVariant];
        } else if (Array.isArray(sourceBlock?.tasks)) {
          sourceTasks = sourceBlock.tasks;
        }
        if (!sourceTasks || sourceTasks.length === 0) return;

        const existing = currentBlocks[blockKey] || {};
        const existingVariants = existing.variants || { A: [], B: [], C: [] };
        const merged = {
          ...existing,
          blockName: existing.blockName || sourceBlock.blockName || '',
          duration: existing.duration || sourceBlock.duration || '',
          activeVariant: existing.activeVariant || targetVariant,
          variants: {
            A: existingVariants.A || [],
            B: existingVariants.B || [],
            C: existingVariants.C || [],
          },
          tasks: existing.tasks || [],
        };
        merged.variants[targetVariant] = JSON.parse(JSON.stringify(sourceTasks));
        mergedBlocks[blockKey] = merged;
      });

      if (Object.keys(mergedBlocks).length === 0) {
        setPushResult({ success: false, message: `Nothing in clipboard for ${variantLabels[targetVariant]}` });
        setTimeout(() => setPushResult(null), 3000);
        return;
      }

      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        if (!newData.full_week.dates) newData.full_week.dates = {};
        if (!newData.full_week.dates[targetDateStr]) {
          newData.full_week.dates[targetDateStr] = { data: {} };
        }
        if (!newData.full_week.dates[targetDateStr].data) {
          newData.full_week.dates[targetDateStr].data = {};
        }
        const targetData = newData.full_week.dates[targetDateStr].data;
        Object.keys(mergedBlocks).forEach((blockKey) => {
          targetData[blockKey] = mergedBlocks[blockKey];
        });
        return newData;
      });

      Object.keys(mergedBlocks).forEach((blockKey) => {
        autoSave(targetDateStr, {
          block: blockKey,
          block_data: ensureBlockFields(mergedBlocks[blockKey]),
        });
      });

      const blockCount = Object.keys(mergedBlocks).length;
      setPushResult({
        success: true,
        message: `Pasted ${variantLabels[targetVariant]} into ${blockCount} block${blockCount === 1 ? '' : 's'}`,
      });
      setTimeout(() => setPushResult(null), 3500);
    },
    [clipboard, selectedDate, getDayData, autoSave, variantLabels]
  );

  // ── Navigate to adjacent day ───────────────────────────────────────────

  const navigateDay = useCallback(
    (delta) => {
      if (!selectedDate) return;
      const d = new Date(selectedDate.year, selectedDate.month, selectedDate.day);
      d.setDate(d.getDate() + delta);
      const newYear = d.getFullYear();
      const newMonth = d.getMonth();
      const newDay = d.getDate();
      // If we crossed a month boundary, update the calendar view
      if (newMonth !== viewMonth || newYear !== viewYear) {
        setViewYear(newYear);
        setViewMonth(newMonth);
      }
      setSelectedDate({ day: newDay, month: newMonth, year: newYear });
    },
    [selectedDate, viewMonth, viewYear]
  );

  // ── Update block field (local + debounce save) ─────────────────────────

  const updateBlockField = useCallback(
    (dayName, blockKey, field, value) => {
      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const wd = resolveWritable(newData.full_week, dayName);
        if (!wd?.data?.[blockKey]) return prev;
        wd.data[blockKey][field] = value;
        return newData;
      });

      // Build save payload from current data
      setTimeout(() => {
        const currentBlock = getDayData(dayName)?.data?.[blockKey];
        if (currentBlock) {
          const updated = ensureBlockFields({ ...currentBlock, [field]: value });
          autoSave(dayName, { block: blockKey, block_data: updated });
        }
      }, 0);
    },
    [weeklyPlan, autoSave]
  );

  // ── Update exercise ────────────────────────────────────────────────────

  const updateExercise = useCallback(
    (dayName, blockKey, exIdx, field, value) => {
      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const wd = resolveWritable(newData.full_week, dayName);
        const block = wd?.data?.[blockKey];
        if (!block) return prev;
        // Always write to variants[activeVar] - initialise it from legacy tasks if missing,
        // so different variants stop sharing the same task list.
        const activeVar = block.activeVariant || "A";
        block.variants = block.variants || {};
        if (!Array.isArray(block.variants[activeVar])) {
          block.variants[activeVar] = Array.isArray(block.tasks)
            ? JSON.parse(JSON.stringify(block.tasks))
            : [];
        }
        if (!block.variants[activeVar][exIdx]) return prev;
        block.variants[activeVar][exIdx][field] = value;

        // Debounce save with updated block
        const saveKey = `${dayName}_${blockKey}`;
        if (saveTimers.current[saveKey]) {
          clearTimeout(saveTimers.current[saveKey]);
        }
        pendingSaves.current[saveKey] = {
          day: dayName,
          block: blockKey,
          block_data: ensureBlockFields(block),
        };
        saveTimers.current[saveKey] = setTimeout(async () => {
          const payload = pendingSaves.current[saveKey];
          delete pendingSaves.current[saveKey];
          const token = localStorage.getItem("accessToken");
          lastLocalSaveAt.current = Date.now();
          try {
            const res = await fetch(`${apiPath.baseURL}${apiPath.updateBlock}`, {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            });
            const result = await res.json();
            if (result.status === 200) {
              lastLocalSaveAt.current = Date.now();
              setJustSaved(true);
              setTimeout(() => setJustSaved(false), 1200);
            }
          } catch {
            // silent
          }
        }, 500);

        return newData;
      });
    },
    []
  );

  // ── Add / delete exercise ──────────────────────────────────────────────

  const addExercise = useCallback(
    (dayName, blockKey) => {
      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const block = resolveWritable(newData.full_week, dayName)?.data?.[blockKey];
        if (!block) return prev;
        const activeVar = block.activeVariant || "A";
        block.variants = block.variants || {};
        if (!Array.isArray(block.variants[activeVar])) {
          block.variants[activeVar] = Array.isArray(block.tasks)
            ? JSON.parse(JSON.stringify(block.tasks))
            : [];
        }
        block.variants[activeVar].push(emptyExercise());
        autoSave(dayName, { block: blockKey, block_data: ensureBlockFields(block) });
        return newData;
      });
    },
    [autoSave]
  );

  const deleteExercise = useCallback(
    (dayName, blockKey, exIdx) => {
      setWeeklyPlan((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const block = resolveWritable(newData.full_week, dayName)?.data?.[blockKey];
        if (!block) return prev;
        const activeVar = block.activeVariant || "A";
        block.variants = block.variants || {};
        if (!Array.isArray(block.variants[activeVar])) {
          block.variants[activeVar] = Array.isArray(block.tasks)
            ? JSON.parse(JSON.stringify(block.tasks))
            : [];
        }
        block.variants[activeVar].splice(exIdx, 1);
        if (block.variants[activeVar].length === 0) block.variants[activeVar].push(emptyExercise());
        autoSave(dayName, { block: blockKey, block_data: ensureBlockFields(block) });
        return newData;
      });
    },
    [autoSave]
  );

  // ── Toggle block collapse ──────────────────────────────────────────────

  const toggleCollapse = (blockKey) => {
    setCollapsedBlocks((prev) => ({ ...prev, [blockKey]: !prev[blockKey] }));
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const weeks = getMonthGrid(viewYear, viewMonth);
  const selectedDayNameLegacy = selectedDate ? dateToDayName(selectedDate.year, selectedDate.month, selectedDate.day) : null;
  const selectedDayData = selectedDayName ? (getDayData(selectedDayName) || getDayData(selectedDayNameLegacy)) : null;
  const selectedBlocks = selectedDayName
    ? (getBlocksForDay(selectedDayName).length > 0 ? getBlocksForDay(selectedDayName) : getBlocksForDay(selectedDayNameLegacy))
    : [];

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingOverlay}>Loading programme...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div style={styles.topBar}>
        <div style={styles.topBarInner}>
          {/* Studio logo — centred */}
          {studioLogo && (
            <img src={studioLogo} alt="Studio" style={{ height: 40, maxWidth: 200, objectFit: "contain" }} />
          )}
          {/* Row: month nav (centred) + spreadsheet buttons (right) */}
          <div style={styles.topBarRow}>
            {/* Spacer to push month nav to true centre */}
            <div style={{ flex: 1 }} />
            <div style={styles.monthNav}>
              <button style={styles.navBtn} onClick={prevMonth}>
                <ChevronLeft size={18} />
              </button>
              <div style={styles.monthLabel}>{monthLabel}</div>
              <button style={styles.navBtn} onClick={nextMonth}>
                <ChevronRight size={18} />
              </button>
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
              {isOffline && (
                <span style={styles.offlineBadge}>
                  <WifiOff size={11} />
                  OFFLINE
                </span>
              )}
              {linkedStudios.length > 0 && (
                <>
                  {pushResult && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: pushResult.success ? "#4ade80" : "#f87171" }}>
                      {pushResult.success ? "✅ " : "❌ "}{pushResult.message}
                    </span>
                  )}
                  {/* One push button per variant that has at least one linked studio. */}
                  {["A", "B", "C"]
                    .filter((v) => linkedStudios.some((s) => s.variant === v))
                    .map((v) => (
                      <button
                        key={v}
                        onClick={() => setShowPushConfirm(v)}
                        disabled={isPushing}
                        style={{
                          background: isPushing ? "#374151" : "#1d4ed8",
                          color: "#fff", border: "none", borderRadius: 6,
                          padding: "6px 14px", fontSize: 12, fontWeight: 600,
                          cursor: isPushing ? "not-allowed" : "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isPushing ? "Pushing..." : `📤 Push ${variantLabels[v]}`}
                      </button>
                    ))}
                  <button
                    key="ALL"
                    onClick={() => setShowPushConfirm("ALL")}
                    disabled={isPushing}
                    style={{
                      background: isPushing ? "#374151" : "#0f766e",
                      color: "#fff", border: "none", borderRadius: 6,
                      padding: "6px 14px", fontSize: 12, fontWeight: 700,
                      cursor: isPushing ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isPushing ? "Pushing..." : "📤 PUSH TO ALL"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Calendar Grid ───────────────────────────────────────────────── */}
      <div style={styles.calendarWrap}>
        <div style={styles.calendarGrid}>
          {/* Header row */}
          {DAY_LABELS.map((d) => (
            <div key={d} style={styles.dayHeader}>
              {d}
            </div>
          ))}

          {/* Week rows */}
          {weeks.map((week, wi) =>
            week.map((day, di) => {
              if (!day) {
                return <div key={`empty-${wi}-${di}`} style={styles.emptyCell} />;
              }
              const isSel =
                selectedDate &&
                selectedDate.day === day &&
                selectedDate.month === viewMonth &&
                selectedDate.year === viewYear;
              const iTod = isToday(viewYear, viewMonth, day);
              const iPast = isPast(viewYear, viewMonth, day);
              const dateStr = dateToDateString(viewYear, viewMonth, day);
              const dayNameLegacy = dateToDayName(viewYear, viewMonth, day);
              const blocks = getBlocksForDay(dateStr).length > 0 ? getBlocksForDay(dateStr) : getBlocksForDay(dayNameLegacy);

              return (
                <div
                  key={`day-${day}`}
                  style={styles.dayCell(isSel, iTod, iPast)}
                  onClick={() => handleSelectDay(day)}
                  onMouseEnter={(e) => {
                    if (!isSel) e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSel) {
                      e.currentTarget.style.background = iPast
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.04)";
                    }
                  }}
                >
                  <div style={styles.dayNumber(iTod)}>{day}</div>
                  {blocks.length > 0 && (
                    <div>
                      {blocks.map((bk) => (
                        <span key={bk} style={styles.blockBadge}>
                          {bk.replace("_BLOCK", "")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Selected Day Panel ──────────────────────────────────────────── */}
      {selectedDate && selectedDayName && (
        <div style={styles.dayPanel}>
          {/* Header */}
          <div style={styles.dayPanelHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", color: "#ccc", display: "flex", alignItems: "center" }}
                onClick={() => navigateDay(-1)}
              >
                <ChevronLeft size={16} />
              </button>
              <div style={styles.dayTitle}>
                {formatDateHeader(selectedDate.year, selectedDate.month, selectedDate.day)}
              </div>
              <button
                style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", color: "#ccc", display: "flex", alignItems: "center" }}
                onClick={() => navigateDay(1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              {selectedDate && (() => {
                const prevDate = new Date(selectedDate.year, selectedDate.month, selectedDate.day);
                prevDate.setDate(prevDate.getDate() - 1);
                const prevLabel = prevDate.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
                return (
                  <button style={styles.copyBtn} onClick={() => copyFromPreviousDay(selectedDayName)}>
                    <Copy size={14} />
                    Copy from {prevLabel}
                  </button>
                );
              })()}
              {selectedDate && (
                <button style={styles.copyBtn} onClick={copyDayToClipboard}>
                  <Copy size={14} />
                  Copy this day
                </button>
              )}
              {clipboard && (
                <>
                  <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4 }}>
                    From {clipboard.sourceLabel}:
                  </span>
                  {["A", "B", "C"]
                    .filter((v) => {
                      // Only show paste buttons for variants the clipboard actually has data for
                      return Object.values(clipboard.data || {}).some((b) => {
                        const tasks = b?.variants?.[v];
                        return Array.isArray(tasks) && tasks.length > 0;
                      });
                    })
                    .map((v) => (
                      <button
                        key={v}
                        style={{
                          ...styles.copyBtn,
                          background: "rgba(29,78,216,0.18)",
                          borderColor: "rgba(29,78,216,0.5)",
                          color: "#bfdbfe",
                        }}
                        onClick={() => pasteVariantFromClipboard(v)}
                      >
                        Paste {variantLabels[v]}
                      </button>
                    ))}
                  <button
                    style={{
                      ...styles.copyBtn,
                      background: "transparent",
                      borderColor: "rgba(255,255,255,0.12)",
                      color: "#9ca3af",
                      padding: "4px 8px",
                    }}
                    onClick={() => setClipboard(null)}
                    title="Clear clipboard"
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Workout Name */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
            <label style={{ ...styles.fieldLabel, whiteSpace: "nowrap" }}>Workout Name</label>
            <input
              key={`workout-name-${selectedDayName}`}
              style={{ ...styles.input, flex: 1 }}
              defaultValue={selectedDayData?.name || ""}
              placeholder="e.g. GRIND — Lower Body Strength"
              onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.1)";
                const val = e.target.value;
                setWeeklyPlan((prev) => {
                  if (!prev) return prev;
                  const nd = JSON.parse(JSON.stringify(prev));
                  const wd = resolveWritable(nd.full_week, selectedDayName);
                  if (wd) wd.name = val;
                  return nd;
                });
                autoSave(selectedDayName, { new_title: val });
              }}
            />
          </div>

          {/* Blocks */}
          {selectedBlocks.map((blockKey) => {
            const block = selectedDayData?.data?.[blockKey];
            if (!block) return null;
            const isCollapsed = collapsedBlocks[`${selectedDayName}_${blockKey}`];
            const activeVariant = block.activeVariant || block.variants?.active || "A";
            // Fall back to legacy block.tasks when variants[active] is missing OR empty
            // (a desync state the migration sometimes left behind). Without this guard
            // a stranded block silently disappears from the UI.
            const _fromActive = (block.variants && Array.isArray(block.variants[activeVariant]))
              ? block.variants[activeVariant]
              : null;
            const tasks = (_fromActive && _fromActive.length > 0)
              ? _fromActive
              : (Array.isArray(block.tasks) ? block.tasks : []);

            return (
              <div key={blockKey} style={styles.blockCard}>
                {/* Block header */}
                <div
                  style={styles.blockHeader}
                  onClick={() => toggleCollapse(`${selectedDayName}_${blockKey}`)}
                >
                  <div style={styles.blockTitle}>
                    <span style={styles.blockBadgeLg}>{blockKey.replace("_BLOCK", "")} BLOCK</span>
                    {block.blockName && (
                      <span style={{ color: "#ccc", fontSize: "14px", fontWeight: 500 }}>
                        {block.blockName}
                      </span>
                    )}
                    {block.duration && (
                      <span style={{ color: "#888", fontSize: "12px" }}>{block.duration}</span>
                    )}
                  </div>
                  <div style={styles.blockActions}>
                    <button
                      style={styles.deleteBlockBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete ${blockKey.replace("_", " ")}?`)) {
                          deleteBlock(selectedDayName, blockKey);
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                    {isCollapsed ? <ChevronDown size={16} color="#888" /> : <ChevronUp size={16} color="#888" />}
                  </div>
                </div>

                {/* Block body */}
                {!isCollapsed && (
                  <div style={styles.blockBody}>
                    {/* Variant tabs */}
                    <div style={styles.variantTabs}>
                      {["A", "B", "C"].map((v) => (
                        <div
                          key={v}
                          style={styles.variantTab(activeVariant === v)}
                          onClick={() => {
                            setWeeklyPlan((prev) => {
                              if (!prev) return prev;
                              const nd = JSON.parse(JSON.stringify(prev));
                              const bb = resolveWritable(nd.full_week, selectedDayName)?.data?.[blockKey];
                              if (!bb) return prev;
                              bb.activeVariant = v;
                              // Initialise this variant's tasks array if it doesn't exist - copy from
                              // legacy tasks so the user has a starting point, or empty for a fresh build.
                              bb.variants = bb.variants || {};
                              if (!Array.isArray(bb.variants[v])) {
                                bb.variants[v] = Array.isArray(bb.tasks)
                                  ? JSON.parse(JSON.stringify(bb.tasks))
                                  : [];
                              }
                              // Initialise this variant's timing slot as EMPTY when missing - each
                              // variant represents a different workout that should require explicit
                              // timing. The user wanted no implicit inheritance so they can clearly run
                              // 2-3 different workouts on the same block/day.
                              bb.variantTimings = bb.variantTimings || {};
                              if (!bb.variantTimings[v] || typeof bb.variantTimings[v] !== "object") {
                                bb.variantTimings[v] = {
                                  duration: "",
                                  timeSegments: [],
                                  block_transition: 0,
                                };
                              }
                              const vt = bb.variantTimings[v];
                              if (vt?.duration !== undefined) bb.duration = vt.duration;
                              if (Array.isArray(vt?.timeSegments)) bb.timeSegments = vt.timeSegments;
                              if (vt?.block_transition !== undefined) bb.block_transition = vt.block_transition;
                              return nd;
                            });
                            const vt = block.variantTimings?.[v] || {};
                            autoSave(selectedDayName, { block: blockKey, block_data: ensureBlockFields({ ...block, activeVariant: v, ...(vt.duration !== undefined ? { duration: vt.duration } : {}), ...(Array.isArray(vt.timeSegments) ? { timeSegments: vt.timeSegments } : {}) }) });
                          }}
                        >
                          {variantLabels[v]}
                        </div>
                      ))}
                    </div>

                    {/* Block meta fields */}
                    <div style={styles.fieldRow}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel}>Block Title</label>
                        <input
                          style={styles.input}
                          value={block.blockName || ""}
                          placeholder="e.g. Build"
                          onChange={(e) =>
                            updateBlockField(selectedDayName, blockKey, "blockName", e.target.value)
                          }
                          onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel}>Description</label>
                        <input
                          style={styles.input}
                          value={block.description || ""}
                          placeholder="e.g. STRENGTH | PRIMARY MOVEMENT"
                          onChange={(e) =>
                            updateBlockField(selectedDayName, blockKey, "description", e.target.value)
                          }
                          onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel}>Duration</label>
                        <input
                          style={styles.input}
                          value={(block.variantTimings?.[activeVariant]?.duration ?? block.duration) || ""}
                          placeholder="e.g. 12:00"
                          onChange={(e) => {
                            const val = e.target.value;
                            setWeeklyPlan((prev) => {
                              if (!prev) return prev;
                              const nd = JSON.parse(JSON.stringify(prev));
                              const b = resolveWritable(nd.full_week, selectedDayName)?.data?.[blockKey];
                              if (!b) return prev;
                              b.variantTimings = b.variantTimings || {};
                              b.variantTimings[activeVariant] = { ...(b.variantTimings[activeVariant] || {}), duration: val };
                              if (b.activeVariant === activeVariant) b.duration = val;
                              return nd;
                            });
                            autoSave(selectedDayName, { block: blockKey, block_data: ensureBlockFields({ ...block, variantTimings: { ...(block.variantTimings || {}), [activeVariant]: { ...((block.variantTimings || {})[activeVariant] || {}), duration: val } }, duration: block.activeVariant === activeVariant ? val : block.duration }) });
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel}>Segments (ON/OFF)</label>
                        <input
                          style={styles.input}
                          defaultValue={(() => {
                            const ts = block.variantTimings?.[activeVariant]?.timeSegments ?? block.timeSegments;
                            if (typeof ts === "string") return ts;
                            if (Array.isArray(ts) && ts.length > 0) return segmentsToInputString(ts);
                            return "";
                          })()}
                          key={`seg-${blockKey}-${selectedDayName}-${activeVariant}`}
                          placeholder="e.g. 40/10 or 40/0/40/0 (0 rest = back-to-back)"
                          onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                          onBlur={(e) => {
                            e.target.style.borderColor = "rgba(255,255,255,0.1)";
                            const raw = e.target.value;
                            const parsed = parseSegmentsString(raw);
                            const dur = segmentsToDuration(parsed);
                            const newSegs = parsed.length > 0 ? parsed : [];
                            setWeeklyPlan((prev) => {
                              if (!prev) return prev;
                              const newData = JSON.parse(JSON.stringify(prev));
                              const b = resolveWritable(newData.full_week, selectedDayName)?.data?.[blockKey];
                              if (!b) return prev;
                              b.variantTimings = b.variantTimings || {};
                              b.variantTimings[activeVariant] = { ...(b.variantTimings[activeVariant] || {}), timeSegments: newSegs, ...(dur ? { duration: dur } : {}) };
                              if (b.activeVariant === activeVariant) {
                                b.timeSegments = newSegs;
                                if (dur) b.duration = dur;
                              }
                              return newData;
                            });
                            const updatedVT = { ...(block.variantTimings || {}) };
                            updatedVT[activeVariant] = { ...(updatedVT[activeVariant] || {}), timeSegments: newSegs, ...(dur ? { duration: dur } : {}) };
                            const updated = ensureBlockFields({
                              ...block,
                              variantTimings: updatedVT,
                              ...(block.activeVariant === activeVariant ? { timeSegments: newSegs, ...(dur ? { duration: dur } : {}) } : {}),
                            });
                            autoSave(selectedDayName, { block: blockKey, block_data: updated });
                          }}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel}>Transition (sec)</label>
                        <input
                          style={styles.input}
                          type="number"
                          min="0"
                          max="600"
                          step="5"
                          value={block.variantTimings?.[activeVariant]?.block_transition ?? block.block_transition ?? 0}
                          placeholder="e.g. 60"
                          onChange={(e) => {
                            const n = Math.max(0, Math.min(600, parseInt(e.target.value, 10) || 0));
                            setWeeklyPlan((prev) => {
                              if (!prev) return prev;
                              const nd = JSON.parse(JSON.stringify(prev));
                              const bb = resolveWritable(nd.full_week, selectedDayName)?.data?.[blockKey];
                              if (!bb) return prev;
                              bb.variantTimings = bb.variantTimings || {};
                              bb.variantTimings[activeVariant] = { ...(bb.variantTimings[activeVariant] || {}), block_transition: n };
                              if (bb.activeVariant === activeVariant) bb.block_transition = n;
                              return nd;
                            });
                            const vt = { ...(block.variantTimings || {}) };
                            vt[activeVariant] = { ...(vt[activeVariant] || {}), block_transition: n };
                            autoSave(selectedDayName, { block: blockKey, block_data: ensureBlockFields({ ...block, variantTimings: vt, ...(block.activeVariant === activeVariant ? { block_transition: n } : {}) }) });
                            return;
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                        />
                      </div>
                    </div>
                    {/* Block Notes */}
                    <div style={styles.fieldGroup}>
                      <label style={styles.fieldLabel}>Block Notes / Coaching Cues</label>
                      <textarea
                        key={`notes-${blockKey}-${selectedDayName}`}
                        style={{ ...styles.input, minHeight: "72px", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
                        defaultValue={block.cooldown || ""}
                        placeholder="Type coaching cues, block notes..."
                        onFocus={(e) => (e.target.style.borderColor = "rgba(0,74,173,0.6)")}
                        onBlur={(e) => {
                          e.target.style.borderColor = "rgba(255,255,255,0.1)";
                          updateBlockField(selectedDayName, blockKey, "cooldown", e.target.value);
                        }}
                      />
                    </div>

                    {/* Segments visual preview */}
                    {Array.isArray(block.timeSegments) && block.timeSegments.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "12px" }}>
                        {block.timeSegments.map((seg, si) => (
                          <React.Fragment key={si}>
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: "rgba(0,74,173,0.3)",
                              color: "#6fa8ff",
                            }}>
                              {seg.workingTime}s ON
                            </span>
                            {seg.restTime > 0 && (
                              <span style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                                background: "rgba(243,156,18,0.25)",
                                color: "#f39c12",
                              }}>
                                {seg.restTime}s REST
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* Exercise table */}
                    {activeVariant === "C" ? (
                      /* Hyrox Mode — Stations editor */
                      <div style={{ padding: "8px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <span style={{ color: "#fcdc08", fontWeight: 800, fontSize: 14 }}>HYROX MODE</span>
                          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Stations / Rounds</span>
                        </div>
                        {(block.variants?.C?.stations || []).map((station, sIdx) => (
                          <div key={sIdx} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                            <span style={{ color: "#fcdc08", fontWeight: 700, fontSize: 13, minWidth: 24 }}>{sIdx + 1}.</span>
                            <input
                              style={{ ...styles.tdInput, flex: 1 }}
                              value={station}
                              placeholder="e.g. 1KM ROW"
                              onChange={(e) => {
                                const stations = [...(block.variants?.C?.stations || [])];
                                stations[sIdx] = e.target.value;
                                const updatedVariants = { ...block.variants, C: { stations, mode: "hyrox" } };
                                updateBlockField(selectedDayName, blockKey, "variants", updatedVariants);
                              }}
                            />
                            <button
                              onClick={() => {
                                const stations = (block.variants?.C?.stations || []).filter((_, i) => i !== sIdx);
                                const updatedVariants = { ...block.variants, C: { stations, mode: "hyrox" } };
                                updateBlockField(selectedDayName, blockKey, "variants", updatedVariants);
                              }}
                              style={{ background: "transparent", border: "none", color: "#e74c3c", cursor: "pointer", padding: 4 }}
                            ><Trash2 size={14} /></button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const stations = [...(block.variants?.C?.stations || []), ""];
                            const updatedVariants = { ...block.variants, C: { stations, mode: "hyrox" } };
                            updateBlockField(selectedDayName, blockKey, "variants", updatedVariants);
                          }}
                          style={{ background: "rgba(252,220,8,0.1)", border: "1px solid rgba(252,220,8,0.2)", color: "#fcdc08", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, marginTop: 4 }}
                        >
                          <Plus size={12} style={{ marginRight: 4, verticalAlign: "middle" }} /> Add Station
                        </button>
                      </div>
                    ) : (
                    <>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: "35%" }}>Exercise Name</th>
                          <th style={{ ...styles.th, width: "10%", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                              <span style={{ opacity: block.showSets !== false ? 1 : 0.35 }}>Sets</span>
                              <div
                                onClick={(e) => { e.stopPropagation(); updateBlockField(selectedDayName, blockKey, "showSets", block.showSets === false); }}
                                style={{ width: 30, height: 16, borderRadius: 8, cursor: "pointer", background: block.showSets !== false ? "#2ecc71" : "rgba(255,255,255,0.15)", transition: "background 0.2s", flexShrink: 0 }}
                              />
                            </div>
                          </th>
                          <th style={{ ...styles.th, width: "15%", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                              <span style={{ opacity: block.showReps !== false ? 1 : 0.35 }}>Reps / Interval</span>
                              <div
                                onClick={(e) => { e.stopPropagation(); updateBlockField(selectedDayName, blockKey, "showReps", block.showReps === false); }}
                                style={{ width: 30, height: 16, borderRadius: 8, cursor: "pointer", background: block.showReps !== false ? "#2ecc71" : "rgba(255,255,255,0.15)", transition: "background 0.2s", flexShrink: 0 }}
                              />
                            </div>
                          </th>
                          <th style={{ ...styles.th, width: "12%", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                              <span style={{ opacity: block.showTempo !== false ? 1 : 0.35 }}>Tempo</span>
                              <div
                                onClick={(e) => { e.stopPropagation(); updateBlockField(selectedDayName, blockKey, "showTempo", block.showTempo === false); }}
                                style={{ width: 30, height: 16, borderRadius: 8, cursor: "pointer", background: block.showTempo !== false ? "#2ecc71" : "rgba(255,255,255,0.15)", transition: "background 0.2s", flexShrink: 0 }}
                              />
                            </div>
                          </th>
                          <th style={{ ...styles.th, width: "20%" }}>Notes</th>
                          <th style={{ ...styles.th, width: "4%", textAlign: "center" }}>H</th>
                          <th style={{ ...styles.th, width: "4%", textAlign: "center", fontSize: 11 }}>&#x21D4;</th>
                          <th style={{ ...styles.th, width: "4%" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((ex, exIdx) => (
                          <tr key={exIdx} style={ex.isHeader ? styles.headerRow : {}}>
                            <td style={{ padding: "3px 4px" }}>
                              <input
                                style={ex.isHeader ? styles.headerInput : styles.tdInput}
                                value={ex.name || ""}
                                placeholder={ex.isHeader ? "Section header..." : "Exercise name"}
                                onChange={(e) =>
                                  updateExercise(selectedDayName, blockKey, exIdx, "name", e.target.value)
                                }
                              />
                            </td>
                            <td style={{ padding: "3px 4px" }}>
                              {!ex.isHeader && (
                                <input
                                  style={styles.tdInput}
                                  value={ex.sets || ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateExercise(selectedDayName, blockKey, exIdx, "sets", e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td style={{ padding: "3px 4px" }}>
                              {!ex.isHeader && (
                                <input
                                  style={styles.tdInput}
                                  value={ex.reps || ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateExercise(selectedDayName, blockKey, exIdx, "reps", e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td style={{ padding: "3px 4px" }}>
                              {!ex.isHeader && (
                                <input
                                  style={styles.tdInput}
                                  value={ex.tempo || ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateExercise(selectedDayName, blockKey, exIdx, "tempo", e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td style={{ padding: "3px 4px" }}>
                              {!ex.isHeader && (
                                <input
                                  style={styles.tdInput}
                                  value={ex.notes || ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateExercise(selectedDayName, blockKey, exIdx, "notes", e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td style={{ padding: "3px 4px", textAlign: "center" }}>
                              <div
                                style={styles.hCheckbox(ex.isHeader)}
                                onClick={() =>
                                  updateExercise(
                                    selectedDayName,
                                    blockKey,
                                    exIdx,
                                    "isHeader",
                                    !ex.isHeader
                                  )
                                }
                              >
                                {ex.isHeader ? "H" : ""}
                              </div>
                            </td>
                            <td style={{ padding: "3px 4px", textAlign: "center" }}>
                              <div
                                style={{
                                  width: "20px", height: "20px", borderRadius: "4px",
                                  border: ex.isSplit ? "2px solid #004aad" : "2px solid rgba(255,255,255,0.15)",
                                  background: ex.isSplit ? "rgba(0,74,173,0.3)" : "transparent",
                                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                  color: "#60a5fa", fontWeight: 900, fontSize: 10,
                                }}
                                onClick={() =>
                                  updateExercise(
                                    selectedDayName,
                                    blockKey,
                                    exIdx,
                                    "isSplit",
                                    !ex.isSplit
                                  )
                                }
                              >
                                {ex.isSplit ? "\u21D4" : ""}
                              </div>
                            </td>
                            <td style={{ padding: "3px 4px" }}>
                              <button
                                style={styles.deleteExBtn}
                                onClick={() => deleteExercise(selectedDayName, blockKey, exIdx)}
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <button style={styles.addExRow} onClick={() => addExercise(selectedDayName, blockKey)}>
                      <Plus size={13} />
                      Add Exercise
                    </button>
                    </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add block button */}
          <button style={styles.addBlockBtn} onClick={() => addBlock(selectedDayName)}>
            <Plus size={15} />
            Add Block
          </button>

          {/* Empty state */}
          {selectedBlocks.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#666",
                fontSize: "14px",
              }}
            >
              No blocks programmed for this day. Click "Add Block" to start.
            </div>
          )}
        </div>
      )}

      {/* ── Save banner ─────────────────────────────────────────────────── */}
      {justSaved && <div style={styles.saveBanner}>Saved</div>}

      {/* ── Push to Studios confirmation modal ──────────────────────────── */}
      {/* showPushConfirm is null OR a variant key ('A'/'B'/'C'). Only studios on
          that variant are listed - the push only hits those studios. */}
      {showPushConfirm && (() => {
        const variantKey = showPushConfirm;
        const variantLabel = variantLabels[variantKey];
        const variantStudios = linkedStudios.filter((s) => s.variant === variantKey);
        // Two distinct chip palettes so A/B render visibly different. Variants
        // beyond A/B (e.g. future C) fall back to A's palette.
        const chipBg = variantKey === "B" ? "#1e3a5f" : "#14532d";
        const chipFg = variantKey === "B" ? "#60a5fa" : "#4ade80";
        return (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          }}>
            <div style={{
              background: "#1f2937", borderRadius: 12, padding: 32,
              maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                📤 Push {variantLabel}
              </div>
              <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
                This will push your full programme to the following {variantLabel} studios and override their current plan. Studios can still make their own changes after.
              </div>
              <div style={{ marginBottom: 24, borderRadius: 8, overflow: "hidden", border: "1px solid #374151" }}>
                {variantStudios.map((s, i) => (
                  <div key={s.studio_id} style={{
                    fontSize: 13, color: "#e5e7eb", padding: "10px 14px",
                    background: i % 2 === 0 ? "#111827" : "#1f2937",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span>{s.franchise_id}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                      background: chipBg, color: chipFg,
                    }}>
                      {variantLabel}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowPushConfirm(null)}
                  style={{
                    background: "#374151", color: "#d1d5db", border: "none",
                    borderRadius: 6, padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: 13,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePushToStudios(variantKey)}
                  style={{
                    background: "#1d4ed8", color: "#fff", border: "none",
                    borderRadius: 6, padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: 13,
                  }}
                >
                  Push Now
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Programme;
